import {
  HStack,
  KdsCardUserActivity,
  VStack,
} from "@kazaar-platform/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { useAtomValue } from "jotai";
import { OrderItemPageActivityLogMessage } from "@/components/OrderItemActivityFeed/OrderItemPageActivityLog/OrderItemPageActivityLogMessage";
import { getHumainDurationFromNow } from "@/helpers/dates/getHumainDurationFromNow";
import { useComments } from "@/hooks";
import { userAtom } from "@/store";
import { type CommentsListProps } from "./CommentsList.types";

export const CommentsList = ({
  comments,
  orderItemId,
  orderLogId,
  commentAreaRef,
  orderLog,
  isUserCanEditOrder,
}: CommentsListProps) => {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const userAtomValue = useAtomValue(userAtom);

  const { useAddComment } = useComments();
  const { mutate, isPending: isSubmitingComment } = useAddComment({
    orderItemId,
    orderLogId,
  });

  const onSubmitComment = async (comment: string) => {
    if (!comment) {
      return;
    }

    mutate(
      { content: comment },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["orderItem"],
          });
        },
      }
    );
  };

  return (
    <VStack gap="sp-md">
      <VStack ref={commentAreaRef}>
        <KdsCardUserActivity
          isChatAreaAlwaysOpen
          HeaderReactNode={
            orderLog ? (
              <HStack align="center" gap="sp-xs2">
                <OrderItemPageActivityLogMessage orderItemLog={orderLog} />
              </HStack>
            ) : undefined
          }
          avatarProps={{
            size: "xs2",
            name:
              `${orderLog?.user?.firstName} ${orderLog?.user?.lastName}` || "",
          }}
          timePassedAfterSendingComment={getHumainDurationFromNow(
            new Date(),
            userAtomValue?.preferredLanguage
          )}
          chatAreaProps={{
            onSubmit: onSubmitComment,
            buttonIconProps: {
              isLoading: isSubmitingComment,
            },
            maxLength: 500,
            placeholder: t(
              "expertMode.qualifyDrawer.comments.commentPlaceholder"
            ),
          }}
          isChatAreaEnabled={isUserCanEditOrder}
          isChatAreaOpen
          comments={comments.map((e) => ({
            comment: e.content,
            date: getHumainDurationFromNow(
              new Date(e.createdAt),
              userAtomValue?.preferredLanguage
            ),
            userName: `${e.User.firstName} ${e.User.lastName}`,
            avatarProps: {
              size: "xs2",
              name: `${e.User.firstName} ${e.User.lastName}`,
            },
          }))}
        />
      </VStack>
    </VStack>
  );
};
